import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Loader2, ShieldCheck } from 'lucide-react'
import { UserDataNotice } from '../components/UserDataNoticeEnglish'
import { usePrivacyConsent } from '../components/PrivacyConsentGate'

export function PrivacyDataPage() {
  const navigate = useNavigate()
  const { status, withdraw, deleteData } = usePrivacyConsent()
  const [confirmation, setConfirmation] = useState('')
  const [busy, setBusy] = useState<'withdraw' | 'delete' | null>(null)
  const [error, setError] = useState('')

  const withdrawConsent = async () => {
    if (!window.confirm('After withdrawing, you must confirm the Notice again before continuing. Withdraw now?')) return
    setBusy('withdraw'); setError('')
    try {
      await withdraw()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
      setBusy(null)
    }
  }

  const permanentlyDelete = async () => {
    if (confirmation !== 'DELETE') return
    if (!window.confirm('Permanently delete your locally stored data? This cannot be undone.')) return
    setBusy('delete'); setError('')
    try {
      await deleteData()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
      setBusy(null)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <button type="button" onClick={() => navigate(-1)} className="h-8 w-8 rounded-md bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200">←</button>
          <ShieldCheck className="h-5 w-5 text-blue-600" />
          <h1 className="text-base font-semibold text-gray-900 dark:text-white">User Notice &amp; Data</h1>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-4 p-4">
        <section className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
          <UserDataNotice />
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="font-bold text-gray-950 dark:text-white">Confirmation Status</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Confirmed: {status.accepted_at ? new Date(status.accepted_at).toLocaleString('en-US') : '—'}</p>
          <button type="button" disabled={busy !== null} onClick={() => void withdrawConsent()} className="mt-4 flex w-full items-center justify-center rounded-xl border border-gray-300 px-4 py-3 text-sm font-bold text-gray-800 disabled:opacity-50 dark:border-gray-600 dark:text-gray-100">
            {busy === 'withdraw' ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Withdraw Confirmation'}
          </button>
        </section>

        <section className="rounded-xl border border-red-200 bg-red-50 p-5 dark:border-red-900 dark:bg-red-950/30">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-300"><AlertTriangle className="h-5 w-5" /><h2 className="font-bold">Permanently Delete Data</h2></div>
          <p className="mt-2 text-sm leading-6 text-red-700 dark:text-red-300">This permanently removes locally stored account data, credits, payment history, preferences, favorites, itineraries, share ownership, analytics, and conversations. It cannot remove provider records or data written to a public blockchain.</p>
          <label className="mt-4 block text-xs font-bold text-red-800 dark:text-red-200">Type DELETE to confirm</label>
          <input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="off" className="mt-2 w-full rounded-xl border border-red-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-red-500 dark:border-red-800 dark:bg-gray-900 dark:text-white" />
          <button type="button" disabled={confirmation !== 'DELETE' || busy !== null} onClick={() => void permanentlyDelete()} className="mt-3 flex w-full items-center justify-center rounded-xl bg-red-600 px-4 py-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-40">
            {busy === 'delete' ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Permanently Delete My Data'}
          </button>
          {error && <p className="mt-3 text-sm text-red-700 dark:text-red-300">{error}</p>}
        </section>
      </main>
    </div>
  )
}
